import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import shiftsService from '../../../services/shiftsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const ShiftManagement = ({ isAdmin, clientId }) => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [notification, setNotification] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    start_time: '09:00',
    end_time: '18:00',
    break_duration_minutes: 60,
    is_night_shift: false,
    shift_allowance: 0,
    description: '',
    status: 'active'
  });

  useEffect(() => {
    loadShifts();
  }, [clientId]);

  const loadShifts = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (clientId) {
        filters.client_id = clientId;
      }
      const result = await shiftsService.getShifts(filters);
      if (result?.success) {
        setShifts(result.data || []);
      }
    } catch (error) {
      setNotification({ message: 'Failed to load shifts', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.code || !formData.start_time || !formData.end_time) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const result = await shiftsService.createShift(formData);
      if (result?.success) {
        setNotification({ message: 'Shift created successfully', type: 'success' });
        setShowCreateModal(false);
        resetForm();
        loadShifts();
      } else {
        setNotification({ message: result?.error || 'Failed to create shift', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.code || !formData.start_time || !formData.end_time) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    try {
      setLoading(true);
      const result = await shiftsService.updateShift(editingShift.id, formData);
      if (result?.success) {
        setNotification({ message: 'Shift updated successfully', type: 'success' });
        setEditingShift(null);
        resetForm();
        loadShifts();
      } else {
        setNotification({ message: result?.error || 'Failed to update shift', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this shift?')) return;

    try {
      setLoading(true);
      const result = await shiftsService.deleteShift(id);
      if (result?.success) {
        setNotification({ message: 'Shift deleted successfully', type: 'success' });
        loadShifts();
      } else {
        setNotification({ message: result?.error || 'Failed to delete shift', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      start_time: '09:00',
      end_time: '18:00',
      break_duration_minutes: 60,
      is_night_shift: false,
      shift_allowance: 0,
      description: '',
      status: 'active'
    });
  };

  const openEditModal = (shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      code: shift.code,
      start_time: shift.start_time.substring(0, 5),
      end_time: shift.end_time.substring(0, 5),
      break_duration_minutes: shift.break_duration_minutes || 60,
      is_night_shift: shift.is_night_shift || false,
      shift_allowance: shift.shift_allowance || 0,
      description: shift.description || '',
      status: shift.status || 'active'
    });
    setShowCreateModal(true);
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <Icon name="Lock" size={48} className="mx-auto mb-4 opacity-50" />
        <p>Access restricted. Only administrators can manage shifts.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Shift Management</h4>
          <p className="text-sm text-muted-foreground">Manage work shifts and timings</p>
        </div>
        <Button onClick={() => {
          resetForm();
          setEditingShift(null);
          setShowCreateModal(true);
        }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Create Shift
        </Button>
      </div>

      {/* Shifts Grid */}
      {loading ? (
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">Loading...</p>
        </div>
      ) : shifts.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          <Icon name="Clock" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No shifts found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shifts.map((shift) => (
            <div key={shift.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-semibold text-foreground">{shift.name}</h5>
                  <p className="text-sm text-muted-foreground">Code: {shift.code}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                  shift.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {shift.status}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start:</span>
                  <span className="text-foreground font-medium">{shift.start_time.substring(0, 5)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">End:</span>
                  <span className="text-foreground font-medium">{shift.end_time.substring(0, 5)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Break:</span>
                  <span className="text-foreground font-medium">{shift.break_duration_minutes} min</span>
                </div>
                {shift.is_night_shift && (
                  <div className="flex items-center gap-1 text-amber-600">
                    <Icon name="Moon" size={14} />
                    <span className="text-xs">Night Shift</span>
                  </div>
                )}
                {shift.shift_allowance > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Allowance:</span>
                    <span className="text-foreground font-medium">₹{shift.shift_allowance}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(shift)}
                  className="flex-1"
                >
                  <Icon name="Edit" size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(shift.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Icon name="Trash2" size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-lg font-semibold text-foreground">
                {editingShift ? 'Edit Shift' : 'Create Shift'}
              </h5>
              <Button variant="ghost" size="icon" onClick={() => {
                setShowCreateModal(false);
                setEditingShift(null);
                resetForm();
              }}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Shift Name *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., General Shift"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Shift Code *
                  </label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., GEN"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Start Time *
                  </label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    End Time *
                  </label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Break Duration (minutes)
                  </label>
                  <Input
                    type="number"
                    value={formData.break_duration_minutes}
                    onChange={(e) => setFormData({ ...formData, break_duration_minutes: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Shift Allowance (₹)
                  </label>
                  <Input
                    type="number"
                    value={formData.shift_allowance}
                    onChange={(e) => setFormData({ ...formData, shift_allowance: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_night_shift"
                  checked={formData.is_night_shift}
                  onChange={(e) => setFormData({ ...formData, is_night_shift: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="is_night_shift" className="text-sm font-medium text-foreground">
                  Night Shift
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Shift description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowCreateModal(false);
                  setEditingShift(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button onClick={editingShift ? handleUpdate : handleCreate} disabled={loading}>
                  {loading ? 'Saving...' : (editingShift ? 'Update' : 'Create')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;

